import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./WagersIntroSlider.css";

export default function WagersIntroSlider() {
  const slides = useMemo(
    () => [
      {
        k: "middleman",
        title: "Before you wager",
        body: (
          <>
            <p>
              <strong>GAStreaming is the middle man.</strong> The match is solely
              between you and your opponent.
            </p>
            <p>
              You choose the <strong>game</strong>, <strong>game mode</strong>,{" "}
              <strong>platform</strong>, <strong>wager amount</strong>, and{" "}
              <strong>rules</strong> before the wager starts.
            </p>
            <p className="muted">
              Any game. Any platform. You set the stakes — we provide the
              structure and dispute process.
            </p>
          </>
        ),
      },
      {
        k: "terms",
        title: "Set the terms",
        body: (
          <>
            <p>
              Create the wager with your game + mode, your platform, and clear
              rules (timeouts, rematches, settings, etc.).
            </p>
            <p className="muted">
              Once accepted, the terms are locked for that match.
            </p>
          </>
        ),
      },
      {
        k: "confirm",
        title: "Confirm the outcome",
        body: (
          <>
            <p>
              After the match, <strong>both players confirm</strong> the result.
            </p>
            <p className="muted">
              Funds release only after confirmation. If something feels off, do
              not agree — submit proof.
            </p>
          </>
        ),
      },
      {
        k: "disputes",
        title: "Disputes & proof",
        body: (
          <>
            <p>
              If there’s a dispute, email{" "}
              <strong>support@gastreaming.live</strong> with footage or
              screenshots proving the outcome.
            </p>
            <p className="muted">
              Include wager details (game, platform, opponent, timestamp) so we
              can resolve it fast.
            </p>
          </>
        ),
      },
      {
        k: "members",
        title: "Members only",
        body: (
          <>
            <p>
              Wagers are available only for paid{" "}
              <strong>Get Active Members</strong>.
            </p>
            <p className="muted">
              Not a member yet?{" "}
              <Link className="memberLink" to="/membership">
                Become a Member
              </Link>
              .
            </p>
          </>
        ),
      },
    ],
    []
  );

  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const [active, setActive] = useState(0);

  const scrollTo = (idx) => {
    const el = slideRefs.current[idx];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  };

  const onPrev = () => scrollTo(Math.max(0, active - 1));
  const onNext = () => scrollTo(Math.min(slides.length - 1, active + 1));

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const onScroll = () => {
      // Find closest slide to left edge for active dot
      const rect = track.getBoundingClientRect();
      let bestIdx = 0;
      let bestDist = Infinity;

      slideRefs.current.forEach((node, i) => {
        if (!node) return;
        const r = node.getBoundingClientRect();
        const dist = Math.abs(r.left - rect.left);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });

      setActive(bestIdx);
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="wagersIntroWrap">
      <div className="wagersIntroHeader">
        <div>
          <div className="wagersIntroKicker">WAGERS</div>
          <h2 className="wagersIntroTitle">Read this before you place a wager</h2>
          <div className="wagersIntroHint">
            Swipe/scroll → or use the arrows
          </div>
        </div>

        <div className="wagersIntroControls">
          <button className="wagersIntroBtn" onClick={onPrev} aria-label="Previous">
            ‹
          </button>
          <button className="wagersIntroBtn" onClick={onNext} aria-label="Next">
            ›
          </button>
        </div>
      </div>

      <div className="wagersIntroTrack" ref={trackRef}>
        {slides.map((s, idx) => (
          <article
            key={s.k}
            className="wagersIntroCard"
            ref={(el) => (slideRefs.current[idx] = el)}
            onClick={() => scrollTo(idx)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && scrollTo(idx)}
          >
            <h3 className="wagersIntroCardTitle">{s.title}</h3>
            <div className="wagersIntroBody">{s.body}</div>
          </article>
        ))}
      </div>

      <div className="wagersIntroDots">
        {slides.map((s, idx) => (
          <button
            key={s.k}
            className={"wagersIntroDot" + (idx === active ? " active" : "")}
            onClick={() => scrollTo(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
